/**
 * Qué rutas se pintan con el ancho real del dispositivo. Una sola lista: la lee
 * `AuthViewportService` al navegar, y `index.html` lleva una COPIA en línea
 * porque su script corre antes de que Angular arranque —sin ella, una recarga en
 * el teléfono pintaba la primera vista a 980 y saltaba al llegar el router—.
 * El comentario «Mantener en sync» ya demostró que no basta: la regla R16 de
 * `.claude/scripts/design-system-lint.ts` (`bun run design:lint`) falla si las
 * dos copias difieren.
 *
 * Mientras dure el programa responsive es una lista BLANCA, ruta a ruta: una
 * ruta entra cuando pasa su compuerta (a 390/768/1024 no desborda el documento,
 * solo desplazan `.dxc-table-wrap`/`.dxc-tabs`/`.dxc-kpis`/`.dxc-rail`, sus
 * acciones primarias miden ≥44 en táctil, sus diálogos tienen la salida a mano,
 * y su geometría a 1440/1280 no cambia), y entra en el mismo cambio que la
 * arregla. Cuando queden pocas heredadas se invierte a una lista de exclusión.
 */

/**
 * Primer segmento de las rutas que usan el shell de auth (ver app.routes.ts).
 * La cadena vacía es la ruta raíz, que resuelve al login.
 *
 * `email-confirmation` queda fuera a propósito: no usa el shell de auth y su
 * bloque `.options` tiene un `width: 700px` fijo, así que con el viewport del
 * dispositivo se desborda. Se agrega aquí cuando se vuelva responsive.
 */
export const AUTH_PATHS: readonly string[] = ['', 'login', 'registry', 'register', 'forgot-password'];

/**
 * Pantallas de la app (fuera del shell de auth) que ya son responsive. Prefijos
 * de la ruta completa, sin `#/`; con `$` al final, la ruta exacta —`app/drafts`
 * es el listado, pero el mismo módulo cuelga de ahí `drayage/create`, que no
 * ha pasado su compuerta—.
 *
 * - El detalle de la carga (prefijo: lleva el id detrás). Con 980px un teléfono
 *   lo pintaba al 40 %, con texto de 5px; a 390 apila sus secciones en una
 *   columna con el canal compacto, el desglose de pago desplaza en su caja y los
 *   documentos van a una columna.
 * - `ui-kit` (con su ruta de marco `ui-kit/frame/<id>`): el catálogo tiene que
 *   poder consultarse en un teléfono.
 * - Los listados de la ola 1, cada uno exacto: listados de fletes con filtros que
 *   ya no se pegan, tablas que pasan a tarjeta en un teléfono, el diálogo
 *   «Agregar operador» con su pie alcanzable.
 * - Los formularios y ajustes de la ola 2: cada pestaña de Ajustes (con sus
 *   pestañas interiores y diálogos), el alta de tarifa pactada y el alta de
 *   flete. `app/shipment` y `app/shipment/drayage` son alias que pintan el
 *   listado.
 * - El resto, en la ola 3: complemento de pago (y su diálogo de timbrado como
 *   ruta; `app/invoice` redirige a él), incentivos e invitación, preguntas
 *   frecuentes (dentro y fuera del marco), el borrador con su asistente y las
 *   dos páginas públicas, `customer-tracking` y `recompensa`.
 */
export const RESPONSIVE_ROUTES: readonly string[] = [
  'ui-kit',
  // Ola 1 (22-10-2026): marco y listados.
  'app/dashboard$',
  'app/shipment/drayage/list$',
  'app/shipment/drayage/myDrayages$',
  'app/shipment/drayage/drafts$',
  'app/shipment/drayage/csvHistory$',
  'app/drafts$',
  'app/assets/driver$',
  'app/settings/users$',
  'app/settings/locations$',
  'app/shipment$',
  'app/shipment/drayage$',
  // Ola 2 (22-10-2026): formularios y ajustes.
  'app/settings/profile-and-company$',
  'app/settings/hub/documents$',
  'app/settings/agreed-rates$',
  'app/settings/agreed-rates/new$',
  'app/settings/policies$',
  'app/settings/vehicle-settings$',
  'app/settings/gps-settings$',
  'app/settings/branix-settings$',
  'app/settings/emails$',
  'app/shipment/drayage/create$',
  // Ola 3 (22-10-2026): el resto. El borrador es prefijo: lleva el id detrás.
  'app/invoice$',
  'app/invoice/list-invoices$',
  'app/invoice/payment-plugin$',
  'app/incentives$',
  'app/incentives/invite$',
  'app/faq$',
  'app/draft/',
  'faq$',
  'customer-tracking$',
  'recompensa$',
  // Cierre en develop (22-10-2026): el detalle de la carga, diseño de develop.
  'app/shipment/drayage/newdetails/',
];

/** ¿La ruta (sin `#/` ni consulta) se pinta con el ancho del dispositivo? */
export function isResponsiveRoute(route: string): boolean {
  return AUTH_PATHS.includes(route.split('/')[0])
    || RESPONSIVE_ROUTES.some(entry => entry.endsWith('$') ? route === entry.slice(0, -1) : route.startsWith(entry));
}
